import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Minus, PackageSearch, Percent, Plus, ScanBarcode, ShoppingCart, Trash2, UserRound, X } from 'lucide-react';
import { formatMoney, formatQty, unitAllowsDecimals, UNITS, type CurrencyCode, type Discount, type PromotionRule } from '@tengesa/shared';
import { api, ApiError } from '../../lib/api';
import type { ProductDto, SaleDetail } from '../../lib/types';
import { useSession } from '../../app/session';
import { useCurrentBranch } from '../../app/Shell';
import { Button, Dialog, ErrorNotice, Field, Select, TextInput, useToast } from '../../ui';
import { MoneyInput, QtyInput, useDebounced } from '../../ui/inputs';
import { addToCart, emptyCart, priceLocal, type Cart, type CartLine } from './cart';
import { PaymentDialog } from './PaymentDialog';
import { Receipt, printReceipt } from '../sales/Receipt';
import { CashMovementDialog, CloseShiftDialog, OpenShiftCard, useCurrentShift } from '../shifts/shared';
import { CustomerPicker, type PickedCustomer } from '../customers/CustomerPicker';

export function PosPage() {
  const { me, can } = useSession();
  const { branch } = useCurrentBranch();
  const currency = me.business.baseCurrency as CurrencyCode;
  const money = (c: number) => formatMoney(c, currency);
  const toast = useToast();

  const [cart, setCart] = useState<Cart>(emptyCart);
  const [search, setSearch] = useState('');
  const [weighing, setWeighing] = useState<ProductDto | null>(null);
  const [editing, setEditing] = useState<CartLine | null>(null);
  const [saleDiscountOpen, setSaleDiscountOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const [done, setDone] = useState<SaleDetail | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [customer, setCustomer] = useState<PickedCustomer | null>(null);
  const [pickingCustomer, setPickingCustomer] = useState(false);
  const [shiftAction, setShiftAction] = useState<'cash' | 'close' | null>(null);
  const scanRef = useRef<HTMLInputElement>(null);
  const shift = useCurrentShift(branch?.id);

  const promotions = useQuery({
    queryKey: ['active-promotions'],
    queryFn: () => api.get<PromotionRule[]>('/api/promotions/active'),
    enabled: me.business.enabledModules.includes('promotions'),
    staleTime: 60_000,
  });
  const priced = useMemo(() => priceLocal(cart, me.business, promotions.data ?? []), [cart, me.business, promotions.data]);

  const focusScan = useCallback(() => setTimeout(() => scanRef.current?.focus(), 0), []);

  const add = useCallback(
    (p: ProductDto, qtyMilli?: number) => {
      if (p.isVariantParent) {
        setSearch(p.name);
        return;
      }
      if (qtyMilli == null && unitAllowsDecimals(p.unit)) {
        setWeighing(p);
        return;
      }
      setCart((c) => addToCart(c, p, qtyMilli ?? 1000));
      setSearch('');
      setScanError(null);
      focusScan();
    },
    [focusScan],
  );

  const dq = useDebounced(search.trim(), 120);
  const results = useQuery({
    queryKey: ['pos-search', dq, branch?.id],
    queryFn: () => api.get<{ items: ProductDto[] }>(`/api/products?q=${encodeURIComponent(dq)}&sellable=true&limit=24&branchId=${branch?.id}`),
    enabled: dq.length >= 2 && !!branch,
  });

  const onScan = async (e: FormEvent) => {
    e.preventDefault();
    const code = search.trim();
    if (!code) return;
    try {
      const res = await api.get<ProductDto & { scaleQtyMilli?: number; scalePriceCents?: number }>(
        `/api/products/lookup?code=${encodeURIComponent(code)}&branchId=${branch?.id ?? ''}`,
      );
      add(res, res.scaleQtyMilli ?? (unitAllowsDecimals(res.unit) ? undefined : 1000));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // Not a code: leave the text so the search results show.
        if (/^\d{6,}$/.test(code)) setScanError(`No product has the barcode ${code}`);
      } else setScanError(err instanceof Error ? err.message : 'Lookup failed');
    }
  };

  // Keyboard: F9 or Ctrl+Enter opens payment; Escape clears search.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === 'F9' || (e.key === 'Enter' && (e.ctrlKey || e.metaKey))) && cart.lines.length && !paying && !done) {
        e.preventDefault();
        setPaying(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cart.lines.length, paying, done]);

  const newSale = () => {
    setCart(emptyCart());
    setCustomer(null);
    setDone(null);
    focusScan();
  };

  if (!branch) return <ErrorNotice error={new Error('You are not assigned to any open branch, so you cannot sell. Ask a manager to assign you.')} />;
  if (shift.isLoading) return <p className="py-10 text-center text-muted">Checking your till…</p>;
  if (shift.error) return <ErrorNotice error={shift.error} onRetry={() => shift.refetch()} />;
  if (!shift.data) {
    return can('shifts.operate') ? (
      <div className="py-6">
        <OpenShiftCard branchId={branch.id} />
      </div>
    ) : (
      <ErrorNotice error={new Error('You need a till shift to sell, and your role cannot open one. Ask a manager.')} />
    );
  }

  return (
    <div className="-mx-4 -my-6 grid min-h-[calc(100dvh-64px)] sm:-mx-6 lg:-mx-10 lg:-my-8 lg:grid-cols-[minmax(0,1fr)_420px]">
      {/* Left: find products */}
      <section className="flex min-w-0 flex-col border-line p-4 sm:p-6 lg:border-r">
        <form onSubmit={onScan} className="relative">
          <ScanBarcode className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-muted" aria-hidden />
          <input
            ref={scanRef}
            autoFocus
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setScanError(null);
            }}
            onKeyDown={(e) => e.key === 'Escape' && setSearch('')}
            placeholder="Scan a barcode, or type a product name or code"
            aria-label="Scan or search products"
            className="h-14 w-full rounded-[10px] border border-line-strong bg-surface pl-12 pr-4 text-lg shadow-sm focus:border-ledger-500 focus:outline-none focus:ring-2 focus:ring-ledger-500/30"
          />
        </form>
        {scanError && <p className="mt-2 text-sm text-danger" role="alert">{scanError}</p>}

        <div className="mt-4 flex-1">
          {dq.length < 2 ? (
            <div className="flex h-full flex-col items-center justify-center py-16 text-center text-muted">
              <PackageSearch className="mb-3 size-10 text-faint" aria-hidden />
              <p className="font-medium text-ink">Scan items to start a sale</p>
              <p className="mt-1 max-w-sm text-sm">The scanner types into the box above. You can also search by name or product code.</p>
              <p className="mt-4 text-sm">
                <kbd className="rounded border border-line bg-surface px-1.5 py-0.5 text-xs">F9</kbd> takes payment
              </p>
            </div>
          ) : results.isLoading ? (
            <p className="py-6 text-muted">Searching…</p>
          ) : (
            <ul className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {(results.data?.items ?? []).map((p) => {
                const out = p.stock && p.stock.qtyMilli <= 0;
                return (
                  <li key={p.id}>
                    <button
                      onClick={() => add(p)}
                      className="flex h-full w-full flex-col rounded-[10px] border border-line bg-surface p-3 text-left hover:border-ledger-500/60 hover:bg-ledger-50/50"
                    >
                      <span className="line-clamp-2 font-medium leading-snug">{p.name}</span>
                      <span className="mt-auto flex items-end justify-between gap-2 pt-2">
                        <span className={clsx('text-xs', out ? 'text-danger' : 'text-muted')}>
                          {p.isVariantParent ? 'Choose an option' : p.stock ? `${formatQty(p.stock.qtyMilli)} ${UNITS[p.unit as keyof typeof UNITS]?.short ?? ''} in stock` : p.sku}
                        </span>
                        <span className="font-semibold num">
                          {money(p.priceCents)}
                          {unitAllowsDecimals(p.unit) && <span className="text-xs font-normal text-muted">/{p.unit}</span>}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
              {results.data?.items.length === 0 && <li className="py-6 text-muted">No products match “{dq}”.</li>}
            </ul>
          )}
        </div>
      </section>

      {/* Right: the sale */}
      <aside className="flex flex-col bg-surface lg:sticky lg:top-16 lg:h-[calc(100dvh-64px)]" aria-label="Current sale">
        <div className="flex items-center justify-between gap-2 border-b border-line bg-paper px-5 py-2 text-sm">
          <span className="text-muted">
            {shift.data.number} on {shift.data.tillName}
          </span>
          <span className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => setShiftAction('cash')}>Cash in/out</Button>
            <Button size="sm" variant="ghost" onClick={() => setShiftAction('close')} disabled={cart.lines.length > 0}>Close till</Button>
          </span>
        </div>
        <div className="flex items-center justify-between border-b border-line px-5 py-3">
          <h1 className="flex items-center gap-2 text-lg font-semibold">
            <ShoppingCart className="size-5 text-muted" aria-hidden /> Current sale
          </h1>
          <span className="flex gap-1">
            <Button size="sm" variant="ghost" icon={<UserRound className="size-4" />} onClick={() => setPickingCustomer(true)}>
              {customer ? 'Change' : 'Customer'}
            </Button>
            {cart.lines.length > 0 && (
              <Button size="sm" variant="ghost" onClick={() => { setCart(emptyCart()); setCustomer(null); }}>
                Clear
              </Button>
            )}
          </span>
        </div>
        {customer && (
          <div className="flex items-center justify-between gap-3 border-b border-line bg-ledger-50 px-5 py-2.5">
            <span className="min-w-0">
              <span className="block truncate font-medium">{customer.name}</span>
              <span className="block text-xs text-muted">
                Owes {money(customer.balanceCents)}
                {customer.creditLimitCents > 0 ? ` — ${money(customer.availableCreditCents)} credit available` : ' — no credit facility'}
              </span>
            </span>
            <button aria-label="Remove customer" onClick={() => setCustomer(null)} className="rounded p-1 text-muted hover:text-ink">
              <X className="size-4" />
            </button>
          </div>
        )}

        <ul className="flex-1 divide-y divide-line overflow-y-auto">
          {cart.lines.length === 0 && <li className="px-5 py-10 text-center text-muted">No items yet.</li>}
          {cart.lines.map((l) => {
            const p = priced.lines.find((x) => x.key === l.key)!;
            const decimals = unitAllowsDecimals(l.product.unit);
            const lineDiscount = p.promoDiscountCents + p.manualDiscountCents;
            return (
              <li key={l.key} className="px-5 py-3">
                <div className="flex items-start justify-between gap-3">
                  <button className="min-w-0 text-left" onClick={() => setEditing(l)}>
                    <span className="block font-medium leading-snug hover:underline">{l.product.name}</span>
                    <span className="block text-sm text-muted num">
                      {formatQty(l.qtyMilli)} {UNITS[l.product.unit as keyof typeof UNITS]?.short} × {money(l.unitPriceCents ?? l.product.priceCents)}
                      {l.unitPriceCents != null && ' (price changed)'}
                    </span>
                    {lineDiscount > 0 && (
                      <span className="block text-sm text-ok">
                        {p.promotionName ? `${p.promotionName}: ` : 'Discount: '}−{money(lineDiscount)}
                      </span>
                    )}
                  </button>
                  <span className="font-semibold num">{money(p.grossCents - lineDiscount)}</span>
                </div>
                <div className="mt-2 flex items-center gap-1.5">
                  {!decimals && (
                    <>
                      <IconBtn label="One less" onClick={() => setCart((c) => ({ ...c, lines: c.lines.flatMap((x) => (x.key !== l.key ? [x] : x.qtyMilli > 1000 ? [{ ...x, qtyMilli: x.qtyMilli - 1000 }] : [])) }))}>
                        <Minus className="size-4" />
                      </IconBtn>
                      <IconBtn label="One more" onClick={() => setCart((c) => ({ ...c, lines: c.lines.map((x) => (x.key === l.key ? { ...x, qtyMilli: x.qtyMilli + 1000 } : x)) }))}>
                        <Plus className="size-4" />
                      </IconBtn>
                    </>
                  )}
                  <IconBtn label="Remove" onClick={() => setCart((c) => ({ ...c, lines: c.lines.filter((x) => x.key !== l.key) }))}>
                    <Trash2 className="size-4" />
                  </IconBtn>
                </div>
              </li>
            );
          })}
        </ul>

        <div className="border-t border-line px-5 py-4">
          <dl className="space-y-1 text-[15px]">
            <Row label="Items" value={money(priced.grossCents)} />
            {priced.discountCents > 0 && <Row label="Discounts" value={`−${money(priced.discountCents)}`} tone="ok" />}
            {me.business.vatRegistered && <Row label="VAT included" value={money(priced.vatCents)} muted />}
          </dl>
          <div className="mt-3 flex items-baseline justify-between">
            <span className="text-lg font-medium">Total</span>
            <span className="text-[34px] font-semibold leading-none num">{money(priced.totalCents)}</span>
          </div>
          <div className="mt-4 grid grid-cols-[auto_1fr] gap-2">
            {can('pos.discount') && (
              <Button onClick={() => setSaleDiscountOpen(true)} disabled={!cart.lines.length} icon={<Percent className="size-4" />} aria-label="Discount on whole sale">
                {cart.saleDiscount ? 'Edit discount' : 'Discount'}
              </Button>
            )}
            <Button variant="primary" className={clsx('h-12 text-lg', !can('pos.discount') && 'col-span-2')} disabled={!cart.lines.length} onClick={() => setPaying(true)}>
              Take payment
            </Button>
          </div>
        </div>
      </aside>

      {weighing && (
        <WeightDialog
          product={weighing}
          onClose={() => {
            setWeighing(null);
            focusScan();
          }}
          onAdd={(q) => {
            setCart((c) => addToCart(c, weighing, q));
            setWeighing(null);
            setSearch('');
            focusScan();
          }}
        />
      )}
      {editing && (
        <LineDialog
          line={editing}
          currency={currency}
          onClose={() => {
            setEditing(null);
            focusScan();
          }}
          onSave={(next) => {
            setCart((c) => ({ ...c, lines: c.lines.map((x) => (x.key === next.key ? next : x)) }));
            setEditing(null);
            focusScan();
          }}
        />
      )}
      {saleDiscountOpen && (
        <DiscountDialog
          title="Discount on the whole sale"
          currency={currency}
          value={cart.saleDiscount}
          onClose={() => setSaleDiscountOpen(false)}
          onSave={(d) => {
            setCart((c) => ({ ...c, saleDiscount: d }));
            setSaleDiscountOpen(false);
          }}
        />
      )}
      {paying && branch && (
        <PaymentDialog
          cart={cart}
          customer={customer}
          totalCents={priced.totalCents}
          branchId={branch.id}
          onClose={() => {
            setPaying(false);
            focusScan();
          }}
          onDone={(sale) => {
            setPaying(false);
            setDone(sale);
            toast(`Sale ${sale.number} complete`);
          }}
        />
      )}
      {done && <SaleCompleteDialog sale={done} onNew={newSale} />}
      {pickingCustomer && (
        <CustomerPicker
          onClose={() => {
            setPickingCustomer(false);
            focusScan();
          }}
          onPick={(c) => {
            setCustomer(c);
            setPickingCustomer(false);
            focusScan();
          }}
        />
      )}
      {shiftAction === 'cash' && <CashMovementDialog shift={shift.data} onClose={() => setShiftAction(null)} />}
      {shiftAction === 'close' && <CloseShiftDialog shift={shift.data} onClose={() => setShiftAction(null)} />}
    </div>
  );
}

function Row({ label, value, tone, muted }: { label: string; value: string; tone?: 'ok'; muted?: boolean }) {
  return (
    <div className={clsx('flex justify-between', muted && 'text-muted', tone === 'ok' && 'text-ok')}>
      <dt>{label}</dt>
      <dd className="num">{value}</dd>
    </div>
  );
}

function IconBtn({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button aria-label={label} title={label} onClick={onClick} className="grid size-8 place-items-center rounded-[8px] border border-line text-muted hover:bg-paper hover:text-ink">
      {children}
    </button>
  );
}

function WeightDialog({ product, onClose, onAdd }: { product: ProductDto; onClose: () => void; onAdd: (qtyMilli: number) => void }) {
  const { me } = useSession();
  const [qty, setQty] = useState<number | null>(null);
  const currency = me.business.baseCurrency as CurrencyCode;
  const add = () => qty && onAdd(qty);
  return (
    <Dialog
      open
      onClose={onClose}
      title={product.name}
      description={`${formatMoney(product.priceCents, currency)} per ${UNITS[product.unit as keyof typeof UNITS]?.label.toLowerCase() ?? product.unit}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!qty} onClick={add}>
            Add {qty ? formatMoney(Math.round((product.priceCents * qty) / 1000), currency) : ''}
          </Button>
        </>
      }
    >
      <Field label={`Weight or amount (${UNITS[product.unit as keyof typeof UNITS]?.short ?? product.unit})`} htmlFor="weight" hint="Read it off the scale, e.g. 1.255">
        <QtyInput id="weight" unit={product.unit} value={qty} onChange={setQty} autoFocus onEnter={add} />
      </Field>
    </Dialog>
  );
}

function LineDialog({ line, currency, onClose, onSave }: { line: CartLine; currency: CurrencyCode; onClose: () => void; onSave: (l: CartLine) => void }) {
  const { can } = useSession();
  const [qty, setQty] = useState<number | null>(line.qtyMilli);
  const [price, setPrice] = useState<number | null>(line.unitPriceCents ?? line.product.priceCents);
  const [discount, setDiscount] = useState<Discount | null>(line.manualDiscount);
  const save = () => {
    if (!qty) return;
    onSave({
      ...line,
      qtyMilli: qty,
      unitPriceCents: price != null && price !== line.product.priceCents ? price : null,
      manualDiscount: discount,
    });
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={line.product.name}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={!qty}>
            Update item
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Quantity" htmlFor="line-qty">
          <QtyInput id="line-qty" unit={line.product.unit} value={qty} onChange={setQty} autoFocus onEnter={save} />
        </Field>
        {can('pos.price_override') && (
          <Field label="Unit price" htmlFor="line-price" hint={`Shelf price ${formatMoney(line.product.priceCents, currency)}. Changes are recorded in the audit log.`}>
            <MoneyInput id="line-price" currency={currency} value={price} onChange={setPrice} />
          </Field>
        )}
        {can('pos.discount') && <DiscountFields value={discount} onChange={setDiscount} currency={currency} />}
      </div>
    </Dialog>
  );
}

function DiscountFields({ value, onChange, currency }: { value: Discount | null; onChange: (d: Discount | null) => void; currency: CurrencyCode }) {
  const kind = value?.kind ?? 'none';
  return (
    <div className="grid grid-cols-[150px_1fr] gap-3">
      <Field label="Discount" htmlFor="disc-kind">
        <Select
          id="disc-kind"
          value={kind}
          onChange={(e) => {
            const k = e.target.value;
            onChange(k === 'percent' ? { kind: 'percent', bp: 500 } : k === 'amount' ? { kind: 'amount', cents: 100 } : null);
          }}
        >
          <option value="none">None</option>
          <option value="percent">Percentage</option>
          <option value="amount">Amount off</option>
        </Select>
      </Field>
      {value?.kind === 'percent' && (
        <Field label="Percent off" htmlFor="disc-pct">
          <TextInput
            id="disc-pct"
            inputMode="decimal"
            defaultValue={(value.bp / 100).toString()}
            onChange={(e) => {
              const v = Math.round(Number(e.target.value) * 100);
              if (Number.isFinite(v) && v > 0 && v <= 10_000) onChange({ kind: 'percent', bp: v });
            }}
          />
        </Field>
      )}
      {value?.kind === 'amount' && (
        <Field label="Amount off" htmlFor="disc-amt">
          <MoneyInput id="disc-amt" currency={currency} value={value.cents} onChange={(c) => c && c > 0 && onChange({ kind: 'amount', cents: c })} />
        </Field>
      )}
    </div>
  );
}

function DiscountDialog({ title, currency, value, onClose, onSave }: { title: string; currency: CurrencyCode; value: Discount | null; onClose: () => void; onSave: (d: Discount | null) => void }) {
  const [d, setD] = useState<Discount | null>(value ?? { kind: 'percent', bp: 500 });
  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      description="Shared across the items in proportion to their value. Recorded in the audit log."
      footer={
        <>
          {value && (
            <Button variant="ghost" className="mr-auto" icon={<X className="size-4" />} onClick={() => onSave(null)}>
              Remove discount
            </Button>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => onSave(d)}>
            Apply discount
          </Button>
        </>
      }
    >
      <DiscountFields value={d} onChange={setD} currency={currency} />
    </Dialog>
  );
}

function SaleCompleteDialog({ sale, onNew }: { sale: SaleDetail; onNew: () => void }) {
  const qc = useQueryClient();
  useEffect(() => {
    void qc.invalidateQueries({ queryKey: ['pos-search'] });
  }, [qc]);
  const changeCurrency = (sale.changeCurrency ?? sale.currency) as CurrencyCode;
  return (
    <Dialog
      open
      onClose={onNew}
      title={`Sale ${sale.number} complete`}
      footer={
        <>
          <Button onClick={() => printReceipt()}>Print receipt</Button>
          <Button variant="primary" autoFocus onClick={onNew}>
            New sale
          </Button>
        </>
      }
    >
      <div className="mb-5 rounded-[10px] bg-ledger-50 px-5 py-4 text-center">
        <p className="text-sm text-muted">Change to give</p>
        <p className="text-[40px] font-semibold leading-tight num">{formatMoney(sale.changeCents, changeCurrency)}</p>
      </div>
      <div className="mx-auto max-w-[320px] rounded-[8px] border border-line">
        <Receipt sale={sale} />
      </div>
    </Dialog>
  );
}

